import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { SITE } from "@/constants/site";

const STATUS_STYLES = {
  "en attente": "text-[#B7791F] border-[#B7791F]",
  "confirmée": "text-[#2F855A] border-[#2F855A]",
  "terminée": "text-[#4A5568] border-[#4A5568]",
  "annulée": "text-[#C53030] border-[#C53030]",
};

const formatDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "—");

export default function ReservationBon() {
  const { reference } = useParams();
  const [resa, setResa] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/reservations/ref/${reference}`)
      .then((r) => setResa(r.data))
      .catch(() => setError(true));
  }, [reference]);

  return (
    <div className="min-h-screen bg-[#0D1117] py-10 px-4 flex flex-col items-center" data-testid="bon-page">
      <div className="w-full max-w-2xl flex justify-between items-center mb-6 no-print">
        <Link to="/" data-testid="bon-back-link" className="flex items-center gap-2 font-mono text-xs text-[#8B949E] hover:text-[#F2541B] transition-colors uppercase tracking-wider">
          <ArrowLeft size={15} /> Retour au site
        </Link>
        {resa && (
          <button
            onClick={() => window.print()}
            data-testid="bon-print-btn"
            className="flex items-center gap-2 bg-[#F2541B] hover:bg-[#D9420B] text-white font-display font-bold uppercase tracking-wide text-sm px-6 py-3 rounded-full transition-colors duration-300"
          >
            <Printer size={16} /> Imprimer
          </button>
        )}
      </div>

      {error && (
        <div className="bg-[#161B22] border border-[#21262D] rounded-2xl p-10 text-center max-w-md" data-testid="bon-not-found">
          <p className="font-display font-bold uppercase text-xl text-[#F0F6FC] mb-3">Réservation introuvable</p>
          <p className="text-sm text-[#8B949E]">Vérifiez la référence saisie ou contactez-nous au {SITE.phoneDisplay}.</p>
        </div>
      )}

      {!resa && !error && (
        <p className="font-mono text-sm text-[#8B949E] mt-20" data-testid="bon-loading">Chargement du bon…</p>
      )}

      {resa && (
        <div className="w-full max-w-2xl bg-white text-[#1A202C] rounded-2xl overflow-hidden shadow-2xl print:shadow-none print:rounded-none" data-testid="bon-document">
          <div className="flex items-center justify-between px-8 pt-8 pb-6 border-b-2 border-[#E31B23]">
            <img src="/logo-mark.jpg" alt="Logo TPLM Guyane" className="h-16 w-auto" />
            <div className="text-right">
              <p className="font-display font-extrabold uppercase text-2xl tracking-tight text-[#E31B23]">Bon de réservation</p>
              <p className="font-mono text-xs text-[#4A5568] mt-1">
                Émis le {new Date(resa.created_at).toLocaleDateString("fr-FR")}
              </p>
            </div>
          </div>

          <div className="px-8 py-6">
            <div className="bg-[#F7F7F7] rounded-xl px-5 py-4 flex items-center justify-between mb-7">
              <span className="font-mono text-xs uppercase tracking-wider text-[#4A5568]">Référence</span>
              <span className="font-mono font-bold text-2xl tracking-widest text-[#E31B23]" data-testid="bon-reference">{resa.reference}</span>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-wider text-[#718096]">Client</dt>
                <dd className="font-bold text-base" data-testid="bon-client-name">{resa.name}</dd>
              </div>
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-wider text-[#718096]">Téléphone</dt>
                <dd className="font-mono" data-testid="bon-client-phone">{resa.phone}</dd>
              </div>
              {resa.email && (
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-wider text-[#718096]">Email</dt>
                  <dd className="font-mono">{resa.email}</dd>
                </div>
              )}
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-wider text-[#718096]">Statut</dt>
                <dd>
                  <span className={`inline-block font-mono text-xs uppercase tracking-wider border rounded-full px-3 py-1 ${STATUS_STYLES[resa.status] || ""}`} data-testid="bon-status">
                    {resa.status}
                  </span>
                </dd>
              </div>
              <div className="sm:col-span-2 border-t border-[#E2E8F0] pt-4">
                <dt className="font-mono text-[10px] uppercase tracking-wider text-[#718096]">Prestation réservée</dt>
                <dd className="font-display font-bold uppercase text-lg tracking-tight" data-testid="bon-listing">{resa.listing_title}</dd>
              </div>
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-wider text-[#718096]">Du</dt>
                <dd className="font-bold" data-testid="bon-date-start">{formatDate(resa.date_start)}</dd>
              </div>
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-wider text-[#718096]">Au</dt>
                <dd className="font-bold" data-testid="bon-date-end">{formatDate(resa.date_end)}</dd>
              </div>
              {resa.message && (
                <div className="sm:col-span-2">
                  <dt className="font-mono text-[10px] uppercase tracking-wider text-[#718096]">Précisions</dt>
                  <dd className="text-[#4A5568]">{resa.message}</dd>
                </div>
              )}
            </dl>

            <div className="mt-8 bg-[#FFF5F2] border border-[#E31B23]/30 rounded-xl px-5 py-4">
              <p className="text-sm leading-relaxed text-[#4A5568]">
                <strong className="text-[#1A202C]">Paiement sur place.</strong> Présentez ce bon au bureau
                TPLM Guyane pour finaliser votre réservation et régler sur place — {SITE.address}.
              </p>
            </div>
          </div>

          <div className="px-8 py-5 bg-[#1A202C] text-white flex flex-wrap justify-between gap-3 font-mono text-[10px] uppercase tracking-wider">
            <span>{SITE.fullName} — SIREN {SITE.siren}</span>
            <span>{SITE.phoneDisplay} · {SITE.email}</span>
          </div>
        </div>
      )}
    </div>
  );
}
